import {
  Button,
  Callout,
  Card,
  FormGroup,
  InputGroup,
  MenuItem,
  ProgressBar,
} from "@blueprintjs/core";
import { Select } from "@blueprintjs/select";
import {
  getComponentHelperText,
  getComponentIntent,
  ICON_TYPE_TO_DISPLAY,
  MediaImage,
  type SaveToolbarProps,
} from "@package/ui-apollo-components";
import { HeaderCreateEdit } from "@package/ui-components";
import {
  CreateTagDocument,
  DeleteTagDocument,
  GetTagDocument,
  GetTagGridDocument,
  GetTagListNameIdDocument,
  IconType,
  UpdateTagDocument,
  useGetTagQuery,
  type Tag,
} from "@package/ui-gql/index-data-access";
import { useUpdateSelectedOrganization } from "@package/ui-organization";
import { type ValidationError } from "class-validator";
import { produce, type WritableDraft } from "immer";
import { useCallback, useEffect, useState } from "react";
import {
  EDIT_CARD_WIDTH_CLASS,
  SidebarEntryPath,
} from "../../../../constants/contants";
import { MediaTagSelector } from "../../../MediaTagSelector/MediaTagSelector";
import { SaveToolbar } from "../../../SaveToolbar/SaveToolbar";

const defaultTag: Tag = {
  icon: "",
  iconType: null,
  mediaIcon: null,
  organization: "",
};

type TagEditProps = {
  id?: string;
  toolbarProps?: Partial<SaveToolbarProps>;
};

export function TagEdit({ id, toolbarProps }: TagEditProps) {
  const [tagState, setTagState] = useState<Tag>(defaultTag);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [errors, setErrors] = useState<ValidationError[]>([]);

  const updateTag = useCallback(
    (callback: (draft: WritableDraft<Tag>) => void) => {
      setTagState((prev) => produce(prev, callback));
    },
    [],
  );

  const {
    data: dataTag,
    loading: loadingTag,
    error: errorTag,
  } = useGetTagQuery({
    variables: {
      id: id ?? "",
    },
    skip: !id,
  });

  useEffect(
    function effectOnTagDataChanged() {
      if (dataTag?.item) {
        setTagState(dataTag.item);
      }
    },
    [dataTag?.item],
  );

  useUpdateSelectedOrganization(dataTag?.item?.organization);

  if (loadingTag) {
    return (
      <Card elevation={1}>
        <ProgressBar intent="primary" stripes animate />
      </Card>
    );
  }

  if (errorTag) {
    return (
      <Callout intent="danger" title="Failed Loading Tag">
        <p>Failed loading tag item: {id}</p>
      </Callout>
    );
  }

  return (
    <div className="flex flex-col">
      <HeaderCreateEdit
        editing={!!id}
        loading={loadingTag}
        title="Tag"
        name={dataTag?.item?.title}
      />
      <Card elevation={1} className="mt-4">
        <div className={`${EDIT_CARD_WIDTH_CLASS}`}>
          <FormGroup
            label="Name"
            labelFor="control-name"
            labelInfo="(required)"
            disabled={disabled}
            intent={getComponentIntent<Tag>("title", errors)}
            helperText={getComponentHelperText<Tag>("title", errors)}
          >
            <InputGroup
              disabled={disabled}
              id="control-name"
              placeholder="Name"
              value={tagState.title ?? ""}
              onValueChange={(value) =>
                updateTag((draft) => {
                  draft.title = value;
                })
              }
            />
          </FormGroup>
        </div>
      </Card>
      <Card className="mt-4">
        <div className={`${EDIT_CARD_WIDTH_CLASS}`}>
          <FormGroup
            disabled={disabled}
            label="Icon Type"
            intent={getComponentIntent<Tag>("iconType", errors)}
            helperText={getComponentHelperText<Tag>("iconType", errors)}
          >
            <Select<IconType>
              activeItem={tagState.iconType}
              disabled={disabled}
              fill
              filterable={false}
              items={Object.values(IconType)}
              itemRenderer={(type, { handleClick, handleFocus, modifiers }) => (
                <MenuItem
                  active={modifiers.active}
                  disabled={modifiers.disabled}
                  key={type}
                  onClick={handleClick}
                  onFocus={handleFocus}
                  roleStructure="listoption"
                  text={ICON_TYPE_TO_DISPLAY[type]}
                />
              )}
              popoverProps={{ minimal: true }}
              onItemSelect={(type) =>
                updateTag((draft) => {
                  draft.iconType = type;
                })
              }
            >
              <Button
                alignText="left"
                disabled={disabled}
                fill
                minimal
                outlined
                rightIcon="caret-down"
                text={
                  tagState.iconType
                    ? ICON_TYPE_TO_DISPLAY[tagState.iconType]
                    : "Select a Type"
                }
              />
            </Select>
          </FormGroup>
          {(tagState.iconType === IconType.Text ||
            tagState.iconType === IconType.Textmedia) && (
            <FormGroup
              label="Icon Text"
              labelFor="control-icon-text"
              disabled={disabled}
              intent={getComponentIntent<Tag>("icon", errors)}
              helperText={getComponentHelperText<Tag>("icon", errors)}
            >
              <InputGroup
                disabled={disabled}
                id="control-icon-text"
                placeholder="text"
                value={tagState.icon ?? ""}
                onValueChange={(value) =>
                  updateTag((draft) => {
                    draft.icon = value;
                  })
                }
              />
            </FormGroup>
          )}
          {(tagState.iconType === IconType.Media ||
            tagState.iconType === IconType.Textmedia) && (
            <>
              <FormGroup
                disabled={disabled}
                label="Media Icon File"
                labelFor="control-media"
                intent={getComponentIntent<Tag>("mediaIcon", errors)}
                helperText={getComponentHelperText<Tag>("mediaIcon", errors)}
              >
                <MediaTagSelector
                  fill
                  large
                  minimal
                  disabled={disabled}
                  id={tagState.mediaIcon ?? undefined}
                  onRemoveMedia={() => {
                    updateTag((draft) => {
                      draft.mediaIcon = null;
                    });
                  }}
                  onSelectMedia={(mediaId) => {
                    updateTag((draft) => {
                      draft.mediaIcon = mediaId;
                    });
                  }}
                />
              </FormGroup>
              {!!tagState.mediaIcon && (
                <MediaImage id={tagState.mediaIcon} size="max-h-48" />
              )}
            </>
          )}
        </div>
      </Card>
      <Card className="mt-4">
        <div className={`flex ${EDIT_CARD_WIDTH_CLASS}`}>
          <SaveToolbar
            documentCreate={CreateTagDocument}
            documentDelete={DeleteTagDocument}
            documentUpdate={UpdateTagDocument}
            path={SidebarEntryPath.tags}
            isNew={!id}
            refetchQueries={[
              GetTagDocument,
              GetTagGridDocument,
              GetTagListNameIdDocument,
            ]}
            title="Tag"
            validationModel="tag-update"
            variablesCreate={tagState}
            variablesUpdate={tagState}
            variablesDelete={{ id }}
            {...toolbarProps}
            onInflight={(inflight) => {
              setDisabled(inflight);

              if (inflight) {
                setErrors([]);
              }
            }}
            onValidationFailed={(errors) => {
              setErrors(errors);
            }}
          />
        </div>
      </Card>
    </div>
  );
}
